export const BVA_MICROAPP_IDENTITY = {
  displayName: "BvA",
  moduleId: "BvA31",
  frameworkVersion: "V31",
  releaseVersion: "1.0.0",
  federationContainer: "TekLedger-v31-Integration-Framework",
  remoteEntryPublicPath: "/assets/remoteEntry.js",
  exposedModule: "./BvA",
  moduleRoute: "/modules/BvA31",
  hostProject: "tekledger-team/repls/TekLedger-v31-Integration-Framework",
  contractVersion: "1.0",
} as const;

export type BvaPermission =
  | "BvA31:read"
  | "BvA31:write"
  | "BvA31:publish";

export type BvaEnvironment = "development" | "staging" | "production";

export type BvaAuthenticatedRequest = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

type BvaBusinessValue = string | number | boolean | null;

export interface BvaBusinessContext
  extends Readonly<Record<string, BvaBusinessValue>> {
  companyId: number;
  departmentCode: string;
}

export interface BvaHostContextV1 {
  contractVersion: "1.0";
  user: {
    id: string;
    displayName: string;
    email?: string;
  };
  customer: {
    id: string;
    name: string;
    slug: string;
  };
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  environment: BvaEnvironment;
  businessContext: BvaBusinessContext;
  permissions: {
    values: readonly BvaPermission[];
    signature: string;
    issuedAt: string;
    expiresAt: string;
  };
  authenticatedRequest: BvaAuthenticatedRequest;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  apiBasePath?: string;
  support?: {
    correlationId?: string;
    supportSessionId?: string;
  };
}

export type HostContextValidation =
  | { ok: true; context: BvaHostContextV1 }
  | { ok: false; reason: string };

const environments = new Set<BvaEnvironment>([
  "development",
  "staging",
  "production",
]);
const allowedPermissions = new Set<BvaPermission>([
  "BvA31:read",
  "BvA31:write",
  "BvA31:publish",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasIdentity(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.slug)
  );
}

function isSafeApiBasePath(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("://")
  );
}

export function validateBvaHostContext(value: unknown): HostContextValidation {
  if (!isRecord(value)) {
    return { ok: false, reason: "Host context is required." };
  }
  if (value.contractVersion !== BVA_MICROAPP_IDENTITY.contractVersion) {
    return { ok: false, reason: "The host contract version is not supported." };
  }
  if (
    !isRecord(value.user) ||
    !isNonEmptyString(value.user.id) ||
    !isNonEmptyString(value.user.displayName)
  ) {
    return { ok: false, reason: "A valid host user is required." };
  }
  if (!hasIdentity(value.customer) || !hasIdentity(value.tenant)) {
    return { ok: false, reason: "Customer and tenant context are required." };
  }
  if (
    typeof value.environment !== "string" ||
    !environments.has(value.environment as BvaEnvironment)
  ) {
    return { ok: false, reason: "A valid environment is required." };
  }
  if (!isRecord(value.businessContext)) {
    return { ok: false, reason: "Business context is required." };
  }
  if (
    typeof value.businessContext.companyId !== "number" ||
    !Number.isInteger(value.businessContext.companyId) ||
    value.businessContext.companyId <= 0 ||
    !isNonEmptyString(value.businessContext.departmentCode)
  ) {
    return {
      ok: false,
      reason: "Business context must include a company and department scope.",
    };
  }
  if (
    !isRecord(value.permissions) ||
    !Array.isArray(value.permissions.values) ||
    !value.permissions.values.every(
      (permission) =>
        typeof permission === "string" &&
        allowedPermissions.has(permission as BvaPermission),
    ) ||
    !isNonEmptyString(value.permissions.signature) ||
    !isNonEmptyString(value.permissions.issuedAt) ||
    !isNonEmptyString(value.permissions.expiresAt)
  ) {
    return { ok: false, reason: "Signed tenant permissions are required." };
  }
  if (
    value.permissions.values.includes("BvA31:publish") &&
    !value.permissions.values.includes("BvA31:write")
  ) {
    return {
      ok: false,
      reason: "Publish permission requires write permission.",
    };
  }
  if (
    Number.isNaN(Date.parse(value.permissions.expiresAt as string)) ||
    Date.parse(value.permissions.expiresAt as string) <= Date.now()
  ) {
    return { ok: false, reason: "The host permission context has expired." };
  }
  if (
    typeof value.authenticatedRequest !== "function" ||
    typeof value.navigate !== "function"
  ) {
    return {
      ok: false,
      reason: "Host authentication and navigation callbacks are required.",
    };
  }
  if (
    value.apiBasePath !== undefined &&
    !isSafeApiBasePath(value.apiBasePath)
  ) {
    return { ok: false, reason: "The host API path is not allowed." };
  }
  return { ok: true, context: value as unknown as BvaHostContextV1 };
}

export function hasBvaPermission(
  context: BvaHostContextV1,
  permission: BvaPermission,
): boolean {
  return context.permissions.values.includes(permission);
}

function stableBusinessContext(
  value: BvaBusinessContext,
): string {
  return JSON.stringify(
    Object.entries(value).sort(([left], [right]) => left.localeCompare(right)),
  );
}

export function getBvaScopeKey(context: BvaHostContextV1): string {
  return [
    context.customer.id,
    context.tenant.id,
    context.environment,
    context.user.id,
    [...context.permissions.values].sort().join(","),
    stableBusinessContext(context.businessContext),
  ].join("|");
}

export function requiredPermissionForRequest(
  input: RequestInfo | URL,
  init?: RequestInit,
): BvaPermission {
  const method = (init?.method ?? (input instanceof Request ? input.method : "GET"))
    .toUpperCase();
  if (method === "GET" || method === "HEAD" || method === "OPTIONS") {
    return "BvA31:read";
  }
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
  const pathname = new URL(raw, "http://localhost").pathname;
  const publishesOutlook =
    method === "POST" && /\/api\/outlook-review\/?$/.test(pathname);
  return publishesOutlook ||
    /\/(?:publish|promot[^/]*|complete|finali[sz][^/]*|approv[^/]*)(?:\/|$)/i.test(
      pathname,
    )
    ? "BvA31:publish"
    : "BvA31:write";
}

export function isApprovedApiRequest(
  input: RequestInfo | URL,
  apiBasePath = "/api",
  origin?: string,
): boolean {
  const allowedOrigin =
    origin ??
    (typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost");
  const raw =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;
  const target = new URL(raw, allowedOrigin);
  return (
    target.origin === allowedOrigin &&
    (target.pathname === apiBasePath ||
      target.pathname.startsWith(`${apiBasePath}/`))
  );
}

export function createBvaRequestExecutor(context: BvaHostContextV1) {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const apiBasePath = context.apiBasePath ?? "/api";
    if (!isApprovedApiRequest(input, apiBasePath)) {
      throw new Error("This request origin is not approved by TekLedger.");
    }
    const permission = requiredPermissionForRequest(input, init);
    if (!hasBvaPermission(context, permission)) {
      throw new Error("You do not have permission to perform this action.");
    }
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost";
    const raw =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const target = new URL(raw, origin);
    if (target.searchParams.has("companyId")) {
      target.searchParams.set(
        "companyId",
        String(context.businessContext.companyId),
      );
    }
    if (target.searchParams.has("departmentCode")) {
      target.searchParams.set(
        "departmentCode",
        context.businessContext.departmentCode,
      );
    }
    const scopedUrl =
      typeof input === "string" && input.startsWith("/")
        ? `${target.pathname}${target.search}${target.hash}`
        : target.toString();
    const scopedInput =
      input instanceof Request
        ? new Request(scopedUrl, input)
        : input instanceof URL
          ? new URL(scopedUrl)
          : scopedUrl;
    const scopedInit =
      typeof init?.body === "string"
        ? {
            ...init,
            body: scopeJsonBody(init.body, context.businessContext),
          }
        : init;
    return context.authenticatedRequest(scopedInput, scopedInit);
  };
}

function scopeJsonBody(
  body: string,
  businessContext: BvaBusinessContext,
): string {
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }

  let changed = false;
  const scopeValue = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(scopeValue);
    if (!isRecord(value)) return value;
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => {
        if (key === "companyId") {
          changed = true;
          return [key, businessContext.companyId];
        }
        if (key === "departmentCode") {
          changed = true;
          return [key, businessContext.departmentCode];
        }
        return [key, scopeValue(nestedValue)];
      }),
    );
  };

  const scoped = scopeValue(parsed);
  return changed ? JSON.stringify(scoped) : body;
}